import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
    ShieldWarning, Warning, Receipt, Target, ArrowsClockwise,
    ShieldCheck, CaretRight, Clock, CheckSquare, Square, Stack, Lightning, X,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { formatINR, formatDate } from "../../lib/format";
import PageHeader from "../../components/layout/PageHeader";
import EmptyState from "../../components/EmptyState";
import RoleGuard from "../../components/RoleGuard";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import { cn as classJoin } from "../../lib/utils";

export default function ReconciliationFlags() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState("tds_not_recorded");
    const [selected, setSelected] = useState(new Set());
    const [bulkOpen, setBulkOpen] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data: d } = await api.get("/billing/reports/reconciliation/flags");
            setData(d);
            setSelected(new Set());
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load reconciliation flags"));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

    const toggle = (key) => {
        const next = new Set(selected);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        setSelected(next);
    };

    const toggleAll = (ids) => {
        const next = new Set(selected);
        const allSelected = ids.every((k) => next.has(k));
        if (allSelected) {
            ids.forEach((k) => next.delete(k));
        } else {
            ids.forEach((k) => next.add(k));
        }
        setSelected(next);
    };

    useEffect(() => { setSelected(new Set()); /* reset selections when tab changes */ }, [activeTab]);

    const rowIds = useMemo(() => {
        if (!data) return [];
        const rows = data[activeTab] || [];
        if (activeTab === "tds_not_recorded") return rows.map((r) => r.invoice_id);
        if (activeTab === "certificate_pending") return rows.map((r) => r.payment_id);
        if (activeTab === "payment_mismatch") return rows.map((r) => r.invoice_id);
        return [];
    }, [data, activeTab]);

    if (loading && !data) return <div data-testid="reconciliation-page-loading"><PageHeader eyebrow="Reconciliation" title="Loading…" /></div>;
    if (!data) return null;

    const summary = data.summary;
    const TABS = [
        { key: "tds_not_recorded", label: "TDS not recorded", icon: ShieldWarning, tone: "rose", count: summary.tds_not_recorded },
        { key: "certificate_pending", label: "Certificate pending", icon: Clock, tone: "amber", count: summary.certificate_pending },
        { key: "payment_mismatch", label: "Payment mismatch", icon: Warning, tone: "violet", count: summary.payment_mismatch },
    ];
    const rows = data[activeTab] || [];

    return (
        <div data-testid="reconciliation-page">
            <PageHeader
                eyebrow="Compliance · Reconciliation"
                title={
                    <span className="flex items-center gap-3">
                        <ShieldCheck size={22} weight="duotone" className="text-violet-600" />
                        Reconciliation flags
                    </span>
                }
                description="Operational checks that keep cash, revenue and tax layers aligned. Pure read-only — no data is modified by this view."
                actions={
                    <Button variant="outline" size="sm" onClick={load} className="gap-1.5" data-testid="reconciliation-refresh-btn">
                        <ArrowsClockwise size={13} /> Refresh
                    </Button>
                }
            />

            <div className="px-6 sm:px-8 py-8 space-y-6">
                {/* Summary tiles */}
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-3" data-testid="reconciliation-summary">
                    <Kpi label="Total flags" value={summary.total_flags} icon={<Target size={11} />} tone="slate" testid="reconciliation-kpi-total" />
                    {TABS.map((t) => {
                        const Icon = t.icon;
                        const active = activeTab === t.key;
                        return (
                            <button
                                key={t.key}
                                onClick={() => setActiveTab(t.key)}
                                className={classJoin(
                                    "reedos-card p-4 text-left transition-all border-2",
                                    active ? "border-violet-500 ring-1 ring-violet-200" : "border-transparent hover:border-slate-200"
                                )}
                                data-testid={`reconciliation-tile-${t.key}`}
                            >
                                <div className={classJoin("reedos-eyebrow flex items-center gap-1.5",
                                    t.tone === "rose" ? "text-rose-700" : t.tone === "amber" ? "text-amber-700" : "text-violet-700"
                                )}>
                                    <Icon size={11} weight="fill" /> {t.label}
                                </div>
                                <div className={classJoin("font-display font-bold reedos-data text-3xl mt-1 leading-none",
                                    t.tone === "rose" ? "text-rose-700" : t.tone === "amber" ? "text-amber-700" : "text-violet-700"
                                )}>{t.count}</div>
                                <div className="text-[11px] text-slate-500 mt-1">{active ? "Showing" : "Click to view"}</div>
                            </button>
                        );
                    })}
                </div>

                {/* Active list */}
                <div className="reedos-card overflow-hidden" data-testid={`reconciliation-list-${activeTab}`}>
                    <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between gap-3">
                        <div className="reedos-eyebrow flex items-center gap-1.5">
                            {TABS.find((t) => t.key === activeTab)?.label}
                            <span className="text-slate-500">· {rows.length} row{rows.length === 1 ? "" : "s"}</span>
                        </div>
                        <div className="flex items-center gap-2">
                            {activeTab === "tds_not_recorded" && rowIds.length > 0 && (
                                <>
                                    <button
                                        type="button"
                                        onClick={() => toggleAll(rowIds)}
                                        className="h-7 px-2 text-[10px] rounded border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 inline-flex items-center gap-1"
                                        data-testid="reconciliation-select-all"
                                    >
                                        {selected.size === rowIds.length ? <CheckSquare size={11} weight="fill" /> : <Square size={11} />}
                                        {selected.size === rowIds.length ? "Clear all" : "Select all"}
                                    </button>
                                    <RoleGuard permission="payment:write">
                                        <Button
                                            size="sm"
                                            onClick={() => setBulkOpen(true)}
                                            disabled={selected.size === 0}
                                            className="h-7 px-2 text-[10px] gap-1 bg-violet-600 hover:bg-violet-700 disabled:opacity-50"
                                            data-testid="reconciliation-bulk-resolve-btn"
                                        >
                                            <Lightning size={10} weight="fill" /> Bulk resolve ({selected.size})
                                        </Button>
                                    </RoleGuard>
                                </>
                            )}
                        </div>
                    </div>
                    {rows.length === 0 ? (
                        <EmptyState icon={<ShieldCheck size={36} weight="thin" />} title="Clean" subtitle="No anomalies in this category." />
                    ) : (
                        <div className="divide-y divide-slate-100">
                            {activeTab === "tds_not_recorded" && rows.map((r) => {
                                const isSelected = selected.has(r.invoice_id);
                                return (
                                    <div key={r.invoice_id} className={classJoin("px-5 py-3 flex items-center gap-3", isSelected && "bg-violet-50/50")} data-testid={`flag-row-${r.invoice_id}`}>
                                        <button
                                            type="button"
                                            onClick={() => toggle(r.invoice_id)}
                                            className="shrink-0 text-slate-400 hover:text-violet-700"
                                            data-testid={`flag-check-${r.invoice_id}`}
                                        >
                                            {isSelected ? <CheckSquare size={16} weight="fill" className="text-violet-700" /> : <Square size={16} />}
                                        </button>
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm text-slate-900">
                                                <Link to={`/invoices/${r.invoice_id}`} className="font-mono font-semibold hover:underline">{r.invoice_number}</Link>
                                            </div>
                                            <div className="text-xs text-slate-600 mt-0.5">{r.client_name} · <span className="font-mono text-[10px]">{r.client_gstin}</span></div>
                                            <div className="text-[11px] text-slate-500 mt-1">{r.note}</div>
                                        </div>
                                        <div className="text-right shrink-0">
                                            <div className="font-mono font-bold text-sm text-rose-700">{formatINR(r.paid_amount)}</div>
                                            <div className="text-[10px] text-slate-500 mt-0.5">Paid / Invoice {formatINR(r.invoice_amount)}</div>
                                        </div>
                                        <CaretRight size={14} className="text-slate-300" />
                                    </div>
                                );
                            })}
                            {activeTab === "certificate_pending" && rows.map((r) => (
                                <FlagRow
                                    key={r.payment_id}
                                    testid={`flag-row-${r.payment_id}`}
                                    primary={<Link to={`/invoices/${r.invoice_id}`} className="font-mono font-semibold hover:underline">{r.invoice_number}</Link>}
                                    secondary={<span>{r.client_name}</span>}
                                    amount={formatINR(r.tds_amount)}
                                    amountLabel={`TDS · payment ${formatDate(r.payment_date)}`}
                                    meta={<span><strong>{r.days_pending}d pending</strong> · {r.note}</span>}
                                    tone="amber"
                                />
                            ))}
                            {activeTab === "payment_mismatch" && rows.map((r) => (
                                <FlagRow
                                    key={r.invoice_id}
                                    testid={`flag-row-${r.invoice_id}`}
                                    primary={<Link to={`/invoices/${r.invoice_id}`} className="font-mono font-semibold hover:underline">{r.invoice_number}</Link>}
                                    secondary={<span>{r.client_name} · <span className="text-[10px]">status <strong>{r.status}</strong></span></span>}
                                    amount={formatINR(r.drift)}
                                    amountLabel={`Drift (settled ${formatINR(r.settled_amount)} vs ${formatINR(r.effective_total)})`}
                                    meta={r.note}
                                    tone="violet"
                                />
                            ))}
                        </div>
                    )}
                </div>

                <div className="text-[11px] text-slate-500 leading-relaxed border-t border-slate-100 pt-4" data-testid="reconciliation-note">
                    <strong className="text-slate-700">How to clear flags:</strong>
                    <ul className="list-disc pl-5 mt-2 space-y-1">
                        <li><strong>TDS not recorded:</strong> open the invoice, use <em>Adjust TDS</em> on the relevant payment to record the deduction. Or select multiple rows and use <em>Bulk resolve</em>.</li>
                        <li><strong>Certificate pending:</strong> move the TDS lifecycle to <em>Received</em> once Form 16A is in hand.</li>
                        <li><strong>Payment mismatch:</strong> investigate — likely indicates a manual edit; adjust the offending payment's TDS or issue a credit note.</li>
                    </ul>
                </div>
            </div>

            <BulkResolveDialog
                open={bulkOpen}
                onClose={() => setBulkOpen(false)}
                selectedIds={Array.from(selected)}
                flaggedRows={(data?.tds_not_recorded || []).filter((r) => selected.has(r.invoice_id))}
                onResolved={() => { setBulkOpen(false); setSelected(new Set()); load(); }}
            />
        </div>
    );
}

function BulkResolveDialog({ open, onClose, selectedIds, flaggedRows, onResolved }) {
    const [percentage, setPercentage] = useState(10);
    const [note, setNote] = useState("");
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);

    useEffect(() => {
        if (open) {
            setPercentage(10);
            setNote("Bulk reconcile TDS — month-end");
            setResult(null);
        }
    }, [open]);

    const totalPaid = flaggedRows.reduce((a, r) => a + (r.paid_amount || 0), 0);
    const estimatedTds = Math.round(totalPaid * percentage / 100 * 100) / 100;

    const submit = async () => {
        const pct = Number(percentage);
        if (!pct || pct <= 0) {
            toast.error("Enter a TDS percentage");
            return;
        }
        setBusy(true);
        try {
            const targets = selectedIds.map((invoice_id) => ({ invoice_id, tds_percentage: pct }));
            const { data } = await api.post("/billing/payment/bulk-resolve", {
                targets,
                note: note || `Bulk TDS ${pct}%`,
            });
            setResult(data);
            if (data.failed_count === 0 && data.skipped_count === 0) {
                toast.success(`All ${data.updated_count} invoices updated`);
                onResolved();
            } else {
                toast.warning(`${data.updated_count} updated · ${data.failed_count} failed · ${data.skipped_count} skipped — review below`);
            }
        } catch (err) {
            toast.error(formatApiError(err, "Bulk resolve failed"));
        } finally {
            setBusy(false);
        }
    };

    if (!open) return null;
    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent data-testid="bulk-resolve-dialog" className="sm:max-w-lg">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Lightning size={18} weight="duotone" className="text-violet-600" />
                        Bulk resolve · TDS
                    </DialogTitle>
                    <DialogDescription>
                        Apply a single TDS percentage across <strong>{selectedIds.length}</strong> flagged invoice{selectedIds.length === 1 ? "" : "s"}.
                        Each adjustment is validated independently — over-settlement or missing payments are returned as <em>failed</em> without rolling back others.
                    </DialogDescription>
                </DialogHeader>

                {!result ? (
                    <>
                        <div className="space-y-3">
                            <div className="rounded border border-violet-200 bg-violet-50/40 px-3 py-2 text-[11px] text-violet-800">
                                <strong>Scope:</strong> <span className="font-mono">{formatINR(totalPaid)}</span> total paid across {selectedIds.length} invoice{selectedIds.length === 1 ? "" : "s"}.
                                Estimated TDS at <strong>{percentage}%</strong>: <span className="font-mono">{formatINR(estimatedTds)}</span>.
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label className="text-[10px] font-semibold text-slate-500 uppercase">TDS percentage</Label>
                                    <select value={percentage} onChange={(e) => setPercentage(Number(e.target.value))} className="mt-1 w-full h-9 px-2 text-sm border border-slate-200 rounded bg-white font-mono" data-testid="bulk-resolve-percentage">
                                        {[1, 2, 5, 7.5, 10, 15, 20].map((p) => <option key={p} value={p}>{p}%</option>)}
                                    </select>
                                </div>
                                <div>
                                    <Label className="text-[10px] font-semibold text-slate-500 uppercase">Target payment</Label>
                                    <div className="mt-1 h-9 px-2 text-xs leading-9 text-slate-500 border border-slate-200 rounded bg-slate-50">Latest payment on each invoice</div>
                                </div>
                            </div>
                            <div>
                                <Label className="text-[10px] font-semibold text-slate-500 uppercase">Reconciliation note</Label>
                                <Input value={note} onChange={(e) => setNote(e.target.value)} className="mt-1 h-9 text-sm" data-testid="bulk-resolve-note" />
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant="ghost" onClick={onClose} data-testid="bulk-resolve-cancel">Cancel</Button>
                            <Button onClick={submit} disabled={busy} data-testid="bulk-resolve-submit" className="gap-1.5 bg-violet-600 hover:bg-violet-700">
                                <Stack size={13} /> {busy ? "Resolving…" : `Apply ${percentage}% TDS`}
                            </Button>
                        </DialogFooter>
                    </>
                ) : (
                    <>
                        <div className="space-y-2 max-h-[320px] overflow-y-auto" data-testid="bulk-resolve-result">
                            <ResultLine tone="emerald" label="Updated" count={result.updated_count} />
                            <ResultLine tone="rose" label="Failed" count={result.failed_count} />
                            <ResultLine tone="slate" label="Skipped" count={result.skipped_count} />
                            {result.failed.length > 0 && (
                                <div className="pt-2">
                                    <div className="reedos-eyebrow text-rose-700 mb-1">Failures</div>
                                    {result.failed.map((f, i) => (
                                        <div key={i} className="text-[11px] text-rose-800 bg-rose-50 border border-rose-100 rounded px-2 py-1 mb-1" data-testid={`bulk-resolve-failure-${i}`}>
                                            <span className="font-mono font-semibold">{f.invoice_id || f.payment_id}</span>: {f.reason}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        <DialogFooter>
                            <Button onClick={onResolved} data-testid="bulk-resolve-close" className="gap-1.5">
                                <X size={13} /> Close
                            </Button>
                        </DialogFooter>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
}

function ResultLine({ tone, label, count }) {
    const color = tone === "emerald" ? "text-emerald-700" : tone === "rose" ? "text-rose-700" : "text-slate-600";
    return (
        <div className="flex items-center justify-between border-b border-slate-100 pb-2">
            <span className={classJoin("text-sm font-semibold", color)}>{label}</span>
            <span className={classJoin("font-mono font-bold text-lg", color)}>{count}</span>
        </div>
    );
}

function FlagRow({ primary, secondary, amount, amountLabel, meta, tone, testid }) {
    const toneCls = tone === "rose" ? "text-rose-700" : tone === "amber" ? "text-amber-700" : "text-violet-700";
    return (
        <div className="px-5 py-3 flex items-center gap-4" data-testid={testid}>
            <div className="flex-1 min-w-0">
                <div className="text-sm text-slate-900">{primary}</div>
                <div className="text-xs text-slate-600 mt-0.5">{secondary}</div>
                {meta && <div className="text-[11px] text-slate-500 mt-1">{meta}</div>}
            </div>
            <div className="text-right shrink-0">
                <div className={classJoin("font-mono font-bold text-sm", toneCls)}>{amount}</div>
                {amountLabel && <div className="text-[10px] text-slate-500 mt-0.5">{amountLabel}</div>}
            </div>
            <CaretRight size={14} className="text-slate-300" />
        </div>
    );
}

function Kpi({ label, value, icon, tone = "slate", testid }) {
    const TONE = {
        slate: "text-slate-900",
    };
    return (
        <div className="reedos-card p-4" data-testid={testid}>
            <div className="reedos-eyebrow flex items-center gap-1.5">{icon} {label}</div>
            <div className={classJoin("font-display font-bold reedos-data text-3xl mt-1 leading-none", TONE[tone])}>{value}</div>
        </div>
    );
}
